/**
 * IM 连接状态路由插件
 * 统一管理所有 IM 连接状态事件的处理
 *
 * 职责：
 * 1. 监听连接状态事件（connected, disconnected, kickOut, authFailed, error）
 * 2. 更新 IM Store 的连接状态
 * 3. 处理被踢下线、认证失败等特殊情况
 */

import { onMessage, offMessage } from '@/events/messageEvents'
import { IM_EVENT_TYPE } from '@/common/constants/imEventTypes'
import { useIMStore } from '@/stores/im'
import { useUserStore } from '@/stores/user'
import { imManager } from '@/managers/IMManager'
import { showToast } from 'vant'

// ========== 连接状态事件处理函数 ==========

/**
 * 处理连接成功
 */
function handleConnected(event) {
  const { data } = event
  console.log('[IMConnectionRouter] Connected:', data)

  const imStore = useIMStore()
  imStore.isConnected = true
  imStore.isConnecting = false
  imStore.connectionError = null
}

/**
 * 处理连接断开
 * 🔔 触发场景：
 * - token 过期
 * - 服务端主动断开
 * - IM 断了之后 IM 主动重连不上
 * ⚠️ 注意：主动调用 disconnect 方法不会触发此事件
 */
async function handleDisconnected(event) {
  const { data } = event
  console.log('[IMConnectionRouter] Disconnected:', data)

  const userStore = useUserStore()
  if (userStore.isLoggedIn) {
    // 1. 强制退出登录
    console.warn('[IMConnectionRouter] IM disconnected, forcing logout...')
    await userStore.logout(true)

    // 2. 提示用户
    showToast('Connection lost, please login again')
  }
}

/**
 * 处理被踢下线
 */
async function handleKickOut() {
  console.warn('[IMConnectionRouter] Kicked out from another device')

  const imStore = useIMStore()
  const userStore = useUserStore()

  // 1. 断开 IM
  imStore.disconnectIM()

  // 2. 退出登录
  await userStore.logout()

  // 3. 跳转到登录页
  // router.push({
  //   name: 'Login',
  //   query: { reason: 'kick_out' }
  // })

  // 4. 提示用户
  showToast('Your account has been logged in on another device')
}

/**
 * 处理认证失败
 */
async function handleAuthFailed() {
  console.error('[IMConnectionRouter] Authentication failed')

  const imStore = useIMStore()
  const userStore = useUserStore()

  // 尝试刷新 token 并重连
  const refreshed = await userStore.refreshAccessToken()

  if (refreshed) {
    console.log('[IMConnectionRouter] Token refreshed, reconnecting...')
    await imStore.reconnectIM()
  } else {
    console.error('[IMConnectionRouter] Token refresh failed')
    showToast('Authentication failed, please login again')
  }
}

/**
 * 处理错误
 */
function handleError(event) {
  const { data: error } = event
  console.error('[IMConnectionRouter] Error:', error)

  const imStore = useIMStore()
  imStore.connectionError = error?.message || 'Unknown error'
}

/**
 * 处理 Token 刷新
 * 当 IM 服务器通知 token 即将失效时，获取新 token 并更新到 IM SDK
 */
async function handleRefreshToken() {
  console.log('[IMConnectionRouter] Token refresh requested by IM server')

  const userStore = useUserStore()

  try {
    // 1. 调用刷新接口获取新的 access_token 和 zy_token
    const refreshed = await userStore.refreshAccessToken({ hideLoading: true })

    if (!refreshed) {
      console.error('[IMConnectionRouter] Failed to refresh token')
      return
    }

    // 2. 获取新的 zy_token
    const { zy_token } = userStore.getImData()

    if (!zy_token) {
      console.error('[IMConnectionRouter] New zy_token not found after refresh')
      return
    }

    // 3. 更新 IM SDK 的 token
    imManager.updateToken(zy_token)
    console.log('[IMConnectionRouter] IM token updated successfully')
  } catch (error) {
    console.error('[IMConnectionRouter] Error refreshing IM token:', error)
  }
}

// ========== Plugin 定义 ==========

export default {
  install(app) {
    console.log('[IMConnectionRouter] Installing IM connection router plugin...')

    // 注册连接状态监听器
    onMessage(IM_EVENT_TYPE.CONNECTED, handleConnected)
    onMessage(IM_EVENT_TYPE.DISCONNECTED, handleDisconnected)
    onMessage(IM_EVENT_TYPE.KICK_OUT, handleKickOut)
    onMessage(IM_EVENT_TYPE.AUTH_FAILED, handleAuthFailed)
    onMessage(IM_EVENT_TYPE.ERROR, handleError)
    onMessage(IM_EVENT_TYPE.REFRESH_TOKEN, handleRefreshToken)

    console.log('[IMConnectionRouter] IM connection router installed successfully')

    // ========== 清理函数 ==========
    const originalUnmount = app.unmount
    app.unmount = function () {
      console.log('[IMConnectionRouter] Cleaning up connection listeners...')

      offMessage(IM_EVENT_TYPE.CONNECTED, handleConnected)
      offMessage(IM_EVENT_TYPE.DISCONNECTED, handleDisconnected)
      offMessage(IM_EVENT_TYPE.KICK_OUT, handleKickOut)
      offMessage(IM_EVENT_TYPE.AUTH_FAILED, handleAuthFailed)
      offMessage(IM_EVENT_TYPE.ERROR, handleError)
      offMessage(IM_EVENT_TYPE.REFRESH_TOKEN, handleRefreshToken)

      return originalUnmount.apply(this, arguments)
    }
  }
}

// ========== 导出处理函数（便于测试） ==========
export {
  handleConnected,
  handleDisconnected,
  handleKickOut,
  handleAuthFailed,
  handleError,
  handleRefreshToken
}
